import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { history } from '../../../utils';
import { Option, SearchChip } from '../../../ts/interface/components/table.interface';
import useUrlState from '../../../hooks/use-url-state';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import {
  MovaticFieldGroupAsyncSelect,
  MovaticFieldGroupSelectFilter,
} from '../../../components/Redesing/movatic-field-group';
import { problemsColumns } from '../../../tableColumns/problemColumns';
import { Card as CardMui } from '@mui/material';
import useOrganization from '../../../hooks/use-organization';
import useSearchDebounce from '../../../hooks/search/useSearchDebounce';
import CustomAsyncToast from '../../../components/Redesing/custom-async-toast';
import useExportProblems from '../../../hooks/problems/query/useExportProblems';
import OrganizationSystemMultiSelect from '../../../components/system/organization-system-multi-select';
import { ISystem } from '../../../ts/interface/system.interface';
import { useKeysetPagination } from '../../../hooks/use-keyset-pagination';
import {
  useSupportTicketsRelayQuery,
  mapRelaySupportTicketsToIProblems,
  useTagsRelayQuery,
  mapRelayTagsToITags,
  SupportTicketQueryFilter,
} from '../../../hooks/supportTickets/query/V2';

const filterOptions = [
  { value: '0', label: 'Open', disabled: true },
  { value: '1', label: 'Closed' },
];

function OrganizationSupportTickets() {
  const organizationInfo = useOrganization();

  const [pageState, setPageState, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    selectedSystem: [] as { value: string; label: string; original: ISystem }[],
    system: '',
    selectedStatus: {
      label: 'Open',
      value: '0',
    } as Option | null,
    selectedTags: [] as Option[],
    search: '',
    status: '0',
    tags: '',
    sortConfig: {
      key: 'created',
      direction: 'desc',
    } as ISortConfig,
  });
  const [open, setOpen] = useState(false);
  const debouncedSearchTerm = useSearchDebounce(pageState.search);

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const isEnabled = isComplete && Boolean(organizationInfo?.id);

  // Tags (GraphQL, organization-scoped)
  const { data: tagsData, isLoading: isLoadingTagData } = useTagsRelayQuery(
    {
      organizationId: organizationInfo?.id,
      archived: false,
    },
    { enabled: Boolean(organizationInfo?.id) }
  );

  const mappedTags = useMemo(
    () => (tagsData ? mapRelayTagsToITags(tagsData) : []),
    [tagsData]
  );

  const loadedTags = useMemo(
    () =>
      mappedTags.map((item) => ({
        label: item.name,
        value: item.name,
      })),
    [mappedTags]
  );

  // Convert sort config to GraphQL format
  const sortConfig = useMemo(() => {
    const sortKeyMap: Record<string, 'CREATED' | 'STATE' | 'CONTENT'> = {
      created: 'CREATED',
      state: 'STATE',
      content: 'CONTENT',
    };
    const field = sortKeyMap[pageState.sortConfig.key] || 'CREATED';
    const order = pageState.sortConfig.direction === 'asc' ? 'ASC' : 'DESC';
    return [{ field, order: order as 'ASC' | 'DESC' }];
  }, [pageState.sortConfig.key, pageState.sortConfig.direction]);

  // Build server-side GraphQL filter (organization-scoped).
  const supportTicketFilter = useMemo((): SupportTicketQueryFilter | undefined => {
    if (!organizationInfo?.id) return undefined;
    const filter: SupportTicketQueryFilter = { organizationId: organizationInfo.id };

    if (pageState.status) {
      const state = parseInt(pageState.status, 10);
      if (!Number.isNaN(state)) {
        filter.state = state;
      }
    }
    if (pageState.system) {
      filter.systemIds = pageState.system
        .split(',')
        .filter((id: string) => Boolean(id));
    }
    if (pageState.tags) {
      filter.tags = pageState.tags.split(',').filter((tag: string) => Boolean(tag));
    }
    if (debouncedSearchTerm) {
      filter.pattern = debouncedSearchTerm;
    }
    return filter;
  }, [
    organizationInfo?.id,
    pageState.status,
    pageState.system,
    pageState.tags,
    debouncedSearchTerm,
  ]);

  const {
    data: ticketsData,
    isLoading: isLoadingSupportTicketsData,
    isRefetching,
    hasMore: graphqlHasMore,
    count: totalProblems,
    endCursor,
  } = useSupportTicketsRelayQuery(supportTicketFilter, {
    enabled: isEnabled,
    limit: pageState.sizePerPage,
    sort: sortConfig,
    after,
  });

  // Only the current page's rows are shown (server-side pagination).
  const problemsItems = useMemo(
    () => (ticketsData ? mapRelaySupportTicketsToIProblems(ticketsData) : []),
    [ticketsData]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketsData]);

  const { mutateAsync: exportProblems } = useExportProblems();

  const handleExportData = useCallback(() => {
    CustomAsyncToast({
      promise: () =>
        exportProblems({
          organizationId: organizationInfo?.id,
          systemId: pageState.system,
          status: pageState.status,
          tags: pageState.tags.split(','),
          search: debouncedSearchTerm || undefined,
          order: pageState.sortConfig.key,
          ordinance: pageState.sortConfig.direction,
        }),
      successMessage: () => 'Support tickets exported successfully',
      loadingMessage: 'Exporting support tickets...',
      errorMessage: 'Error exporting support tickets',
    });
  }, [exportProblems, organizationInfo?.id, pageState, debouncedSearchTerm]);

  const [chips, setChips] = useState<SearchChip[]>([]);

  // Rebuild the full chip list from the *applied* filters (the same
  // URL-persisted fields that drive the query), so the chips always match the
  // fetched data — including after a refresh and including when a filter was
  // cleared (no chip). Deriving from the applied fields rather than the pending
  // `selected*` values keeps chips and query in lock-step. The default Open
  // status lives in the persisted state itself, so no chip is seeded here.
  useEffect(() => {
    if (isRestoring) return;

    const next: SearchChip[] = [];

    pageState.status
      ?.split(',')
      .filter(Boolean)
      .forEach((value: string) => {
        const option = filterOptions.find((o) => o.value === value);
        next.push({
          label: 'Status',
          field: 'status',
          value,
          displayValue: option?.label ?? value,
          disabled: false,
        });
      });

    pageState.system
      ?.split(',')
      .filter(Boolean)
      .forEach((value: string) => {
        const system = pageState.selectedSystem?.find((s) => s.value === value);
        next.push({
          label: 'System',
          field: 'system',
          value,
          displayValue: system?.label ?? value,
        });
      });

    pageState.tags
      ?.split(',')
      .filter(Boolean)
      .forEach((value: string) => {
        next.push({ label: 'Tags', field: 'tags', value, displayValue: value });
      });

    setChips(next);
  }, [
    isRestoring,
    pageState.status,
    pageState.system,
    pageState.tags,
    pageState.selectedSystem,
  ]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      resetPagination();
      setChips((prevChips) => {
        return prevChips.filter((group) => {
          return group.label !== deletedGroup.label;
        });
      });

      if (deletedGroup.label === 'Status') {
        setPageState((prevState) => ({
          ...prevState,
          status: '',
          selectedStatus: null,
        }));
      }
      if (deletedGroup.label === 'Tags') {
        setPageState((prevState) => ({ ...prevState, tags: '', selectedTags: [] }));
      }

      if (deletedGroup.label === 'System') {
        setPageState((prevState) => ({
          ...prevState,
          sizePerPage: DEFAULT_ROWS_PER_PAGE,
          selectedSystem: [],
          system: '',
        }));
      }
    },
    [setChips, setPageState, resetPagination]
  );

  const handleStatusChange = useCallback(
    (
      values: string[],
      list: { label: string; value: string }[],
      metadata: { label: string; value: string }
    ): void => {
      resetPagination();
      setPageState((prevState) => ({
        ...prevState,
        sizePerPage: DEFAULT_ROWS_PER_PAGE,
        [metadata.value]: values.join(','),
      }));

      setChips((prevChips) => {
        const valuesFound: string[] = [];

        const newChips = prevChips.filter((chip) => {
          if (chip.field !== metadata.value) {
            return true;
          }

          const found = values.includes(chip.value as string);

          if (found) {
            valuesFound.push(chip.value as string);
          }

          return found;
        });

        if (values.length === valuesFound.length) {
          return newChips;
        }

        values.forEach((value) => {
          if (!valuesFound.includes(value)) {
            const option = list.find((option) => option.value.toString() === value);
            newChips.push({
              label: metadata.label,
              field: metadata.value,
              value,
              displayValue: option!.label,
            });
          }
        });
        return newChips;
      });
    },
    [setChips, setPageState, resetPagination]
  );

  const showSystemColumnOnTable = true;

  return (
    <CardMui>
      <PaginationTable
        dataId="problems-list"
        isLoading={
          isLoadingTagData || isLoadingSupportTicketsData || isPaginating
        }
        isLoadingCache={isRefetching}
        padding={{
          pr: 1,
          pt: 1,
        }}
        buttons={[
          {
            label: 'Export',
            onClick: handleExportData,
            startIcon: <FileDownloadIcon />,
          },
        ]}
        customKey={'id'}
        onRowCLick={(id: string) => history.push(`/problems/${id}`)}
        sortDirection={'desc'}
        showFilter
        chips={chips}
        handleChipDelete={handleChipDelete}
        onApplyFilter={() => {
          if (pageState.status !== pageState.selectedStatus?.value) {
            handleStatusChange(
              [pageState.selectedStatus?.value || ''],
              filterOptions,
              { label: 'Status', value: 'status' }
            );
          }

          if (
            pageState.tags !==
            pageState.selectedTags.map((item: Option) => item.value).join(',')
          ) {
            handleStatusChange(
              pageState.selectedTags.map((item: Option) => item.value.toString()),
              loadedTags,
              { label: 'Tags', value: 'tags' }
            );
          }
          if (
            pageState.system !==
            pageState.selectedSystem.map((item) => item.value).join(',')
          ) {
            handleStatusChange(
              pageState.selectedSystem.map((item) => item.value.toString()),
              pageState.selectedSystem,
              { label: 'System', value: 'system' }
            );
          }
        }}
        spaceFilters={
          <>
            <MovaticFieldGroupAsyncSelect
              dataId="Status-single-select"
              label={'Status'}
              description="Select the status to filter the support tickets."
              isLoading={false}
              name={'status_select'}
              id={'status_select'}
              options={filterOptions}
              showClearButton={true}
              value={
                pageState.selectedStatus
                  ? {
                      value: pageState.selectedStatus.value,
                      label: pageState.selectedStatus.label,
                    }
                  : null
              }
              onChange={(
                event: React.SyntheticEvent,
                newValue: { value: string; label: string }
              ) => {
                setPageState((prevState) => ({
                  ...prevState,
                  selectedStatus: newValue,
                }));
              }}
              open={open}
              onOpen={() => {
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
              }}
              isOptionEqualToValue={(option: any, value: any) =>
                option.value === value.value
              }
              getOptionText={(option: any) => option.label}
              handleSearch={() => {}}
            />
            <MovaticFieldGroupSelectFilter
              label={'Tags'}
              isLoading={isLoadingTagData}
              value={pageState.selectedTags}
              onChange={(event, newValue) => {
                setPageState((prevState) => ({
                  ...prevState,
                  selectedTags: newValue,
                }));
              }}
              options={loadedTags}
            />
            <OrganizationSystemMultiSelect
              organization_id={organizationInfo.id}
              selectedSystem={pageState.selectedSystem}
              onChange={(systems) => {
                setPageState((prevState) => ({
                  ...prevState,
                  selectedSystem: systems.map((system) => ({
                    label: system?.name,
                    value: system?.gid,
                    original: system,
                  })),
                }));
              }}
            />
          </>
        }
        sortBy={pageState.sortConfig.key}
        onSortChange={(sort) => {
          resetPagination();
          setPageState((prevState) => ({
            ...prevState,
            sortConfig: { ...sort, direction: sort.direction as 'desc' | 'asc' },
          }));
        }}
        handleSearch={(search) => {
          resetPagination();
          setPageState((prevState) => ({
            ...prevState,
            search: search.target.value,
          }));
        }}
        searchText={pageState.search}
        columns={problemsColumns(showSystemColumnOnTable) as IColumn[]}
        items={problemsItems || []}
        totalItems={totalProblems}
        onPageChange={(page, pageN) => onPageChange(pageN)}
        onRowsPerPageChange={(row) => {
          const pageSize = Number(row.target.value);
          resetPagination();
          setPageState((prevState) => ({
            ...prevState,
            sizePerPage: pageSize,
          }));
        }}
        page={pageNumber}
        rowsPerPage={pageState.sizePerPage}
        searchPlaceholder={'Search'}
        noDataText={`No support tickets`}
        noDataSlice
        localSearch={false}
        hasMore={graphqlHasMore}
      />
    </CardMui>
  );
}

export default OrganizationSupportTickets;
